import React from "react";
import {
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  Document,
  pdf,
} from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: { padding: 20, fontSize: 10 },
  kvLine: { marginBottom: 1, textAlign: "right" },
  logo: { height: 75, width: 150, marginBottom: 10 },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  practiceName: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 4,
  },
  bold: { fontWeight: "bold" },
  section: { marginBottom: 6 },
  title: {
    textAlign: "center",
    fontSize: 14,
    fontWeight: "bold",
    marginVertical: 8,
    textTransform: "uppercase",
  },
  tableHeader: {
    flexDirection: "row",
    borderBottom: 1,
    fontWeight: "bold",
    paddingBottom: 2,
  },
  tableRow: { flexDirection: "row", marginVertical: 2 },
  line: { height: 1, backgroundColor: "#000", marginVertical: 6 },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 4,
  },
  label: {
    fontSize: 10,
    fontWeight: "bold",
    textAlign: "right",
    width: "75%",
  },
  value: {
    fontSize: 10,
    textAlign: "right",
    fontWeight: "bold",
    width: "25%",
  },
  thankYou: { marginTop: 20, textAlign: "center", fontStyle: "italic" },
});

export const ReceiptPDF = ({ receipt }) => {
  if (!receipt) return null;

  const {
    practice = {},
    account = {},
    receiptNumber = "",
    receiptDate = "",
    receiptInvoices = [],
    receiptTotalAmount = 0,
    receiptPaymentMethod = "",
    receiptReference = "",
  } = receipt;

  // Check if this is a credit note
  const isCreditNote = receiptNumber.startsWith("CN-");
  const documentType = isCreditNote ? "Credit Note" : "Receipt";
  const documentLabel = isCreditNote ? "Credit Note #:" : "Receipt #:";

  const isVatRegistered = !!practice?.vatNumber;
  const vatAmount = isVatRegistered ? receiptTotalAmount * 0.15 : 0;

  const PAYMENT_METHOD_LABELS = {
    cash: "Cash",
    creditCard: "Credit Card",
    paymentLink: "Payment Link",
    eft: "EFT",
    unallocatedFunds: "Unallocated Funds",
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header: practice details */}
        <View style={styles.header}>
          {practice?.logoURL && (
            <Image
              style={[styles.logo, { maxHeight: 150, width: "auto" }]}
              src={practice?.logoURL}
            />
          )}
          <View>
            <Text style={styles.practiceName}>
              {practice?.practiceName || ""}
            </Text>
            <Text>
              <Text style={styles.bold}>Practice No: </Text>
              {practice?.practiceNumber || ""}
            </Text>
            <Text>
              <Text style={styles.bold}>HPCSA No: </Text>
              {practice?.hpcsaNumber || ""}
            </Text>
            {isVatRegistered && (
              <Text>
                <Text style={styles.bold}>VAT: </Text>
                {practice?.vatNumber}
              </Text>
            )}
            <View style={styles.line} />
            <Text>
              {practice?.practiceAddress || ""}
              {practice?.practiceAddress2
                ? `, ${practice?.practiceAddress2}`
                : ""}
            </Text>
            <Text>
              {practice?.practiceCity || ""}
              {practice?.practicePostalCode
                ? `, ${practice?.practicePostalCode}`
                : ""}
            </Text>
            <View
              style={[
                styles.section,
                { flexDirection: "row", justifyContent: "space-between" },
              ]}
            />
            <Text>
              <Text style={styles.bold}>Email: </Text>
              {practice?.practiceEmail || ""}
            </Text>
            <Text>
              <Text style={styles.bold}>Tel: </Text>
              {practice?.practicePhoneNumber || ""}
            </Text>
          </View>
        </View>

        {/* Document Title */}
        <Text style={styles.title}>{documentType}</Text>
        <View style={styles.line} />

        {/* Document Info */}
        <View
          style={[
            styles.section,
            { flexDirection: "row", justifyContent: "space-between" },
          ]}
        >
          <Text style={styles.bold}>{documentLabel} {receiptNumber}</Text>
          <Text>Date: {receiptDate}</Text>
        </View>

        {/* Account Details: Received From / Credit Note For */}
        {/* Account Details: Received From / Credit Note For */}
<View
  style={[
    styles.section,
    { flexDirection: "row", justifyContent: "space-between" },
  ]}
>
  {/* Left: person/address */}
  <View style={{ width: "60%" }}>
    <Text style={styles.bold}>
      {isCreditNote ? "Credit Note For:" : "Received From:"}
    </Text>
    <Text>
      {account?.accountFullNames || ""}
      {account?.accountLastName ? ` ${account?.accountLastName}` : ""}
    </Text>
    <Text>
      {account?.accountPostalAddress1 || ""}
      {account?.accountPostalAddress2
        ? `, ${account?.accountPostalAddress2}`
        : ""}
    </Text>
    <Text>
      {account?.accountPostalAddressCity || ""}
      {account?.accountPostalAddressPostalCode
        ? `, ${account?.accountPostalAddressPostalCode}`
        : ""}
    </Text>
  </View>

  {/* Right: Medical Aid / IOD / Private */}
  <View style={{ width: "40%", alignItems: "flex-end" }}>
    {account?.accountMedicalAid ? (
      <>
        <Text style={styles.bold}>Medical Aid</Text>

        <Text style={styles.kvLine}>{account.accountMedicalAid}</Text>

        {(account.accountMedicalAidPlan || account.accountMedicalAidOption) && (
          <Text style={styles.kvLine}>
            {account.accountMedicalAidPlan || ""}
            {account.accountMedicalAidPlan && account.accountMedicalAidOption
              ? " | "
              : ""}
            {account.accountMedicalAidOption || ""}
          </Text>
        )}

        {!!account.accountMedicalAidNumber && (
          <Text style={styles.kvLine}>{account.accountMedicalAidNumber}</Text>
        )}
      </>
    ) : account?.accountIODFund ? (
      <>
        <Text style={styles.bold}>IOD</Text>

        <Text style={styles.kvLine}>{account.accountIODFund}</Text>

        {(account.accountIODPlan || account.accountIODOption) && (
          <Text style={styles.kvLine}>
            {account.accountIODPlan || ""}
            {account.accountIODPlan && account.accountIODOption ? " | " : ""}
            {account.accountIODOption || ""}
          </Text>
        )}

        {!!account.accountIODClaimRefNumber && (
          <Text style={styles.kvLine}>
            <Text style={styles.bold}>Claim Ref: </Text>
            <Text>{account.accountIODClaimRefNumber}</Text>
          </Text>
        )}

        {!!account.accountIODEmployer && (
          <Text style={styles.kvLine}>
            <Text style={styles.bold}>Employer: </Text>
            <Text>{account.accountIODEmployer}</Text>
          </Text>
        )}

        {!!account.accountIODEmployerWCARegNum && (
          <Text style={styles.kvLine}>
            <Text style={styles.bold}>WCA Reg #: </Text>
            <Text>{account.accountIODEmployerWCARegNum}</Text>
          </Text>
        )}

        {!!account.accountIODDateOfInjury && (
          <Text style={styles.kvLine}>
            <Text style={styles.bold}>Date of Injury: </Text>
            <Text>{account.accountIODDateOfInjury}</Text>
          </Text>
        )}
      </>
    ) : (
      <>
        <Text style={styles.bold}>Private</Text>
        <Text style={styles.kvLine}>Private Patient</Text>
      </>
    )}
  </View>
</View>
        <View style={styles.line} />

        {/* Table Header */}
        <View style={styles.tableHeader}>
          <Text style={{ width: "40%" }}>Invoice #</Text>
          <Text style={{ width: "20%", textAlign: "right" }}>Source</Text>
          <Text style={{ width: "20%", textAlign: "right" }}>Amount</Text>
          <Text style={{ width: "20%", textAlign: "right" }}>Total</Text>
        </View>

        {/* Table Rows */}
        {receiptInvoices.map((line, idx) => {
          // Handle both receipt and credit note data structures
          const amount = line.paymentAmount || line.creditNoteAmount || 0;
          const source = normalizePaymentSource(line.paymentSource);
          return (
            <View key={idx} style={styles.tableRow}>
              <Text style={{ width: "40%" }}>
                Invoice - {line.invoiceNumber} ({line.invoiceDate})
              </Text>
              <Text style={{ width: "20%", textAlign: "right" }}>{source}</Text>
              <Text style={{ width: "20%", textAlign: "right" }}>
                R {amount.toFixed(2)}
              </Text>
              <Text style={{ width: "20%", textAlign: "right" }}>
                R {amount.toFixed(2)}
              </Text>
            </View>
          );
        })}

        {/* Payment Method & Reference */}
        <View style={styles.section}>
          <Text>
            <Text style={styles.bold}>
              {isCreditNote ? "Credit Note Method: " : "Payment Method: "}
            </Text>
            {PAYMENT_METHOD_LABELS[receiptPaymentMethod] ||
              receiptPaymentMethod || "N/A"}
          </Text>
          <Text>
            <Text style={styles.bold}>
              {isCreditNote ? "Credit Note Reference: " : "Payment Reference: "}
            </Text>
            {receiptReference}
          </Text>
        </View>

        {/* Totals */}
        <View style={styles.line} />
        <View style={styles.totalRow}>
          <View style={styles.label} />
          <View style={styles.value}>
            {isVatRegistered && (
                          <>
                            <Text
                              style={{
                                fontSize: 10,
                                fontWeight: "normal",
                                textAlign: "right",
                              }}
                            >
                             VAT: R {vatAmount.toFixed(2)}
                            </Text>
                            <View
                              style={{
                                height: 1,
                                backgroundColor: "#ccc",
                                marginVertical: 4,
                              }}
                            />
                          </>
                        )}
            <Text style={{ fontWeight: "bold" }}>
              Total: R {receiptTotalAmount.toFixed(2)}
            </Text>
          </View>
        </View>

        {/* Thank you note */}
        <Text style={styles.thankYou}>
          {isCreditNote 
            ? "Thank you for your business." 
            : "Thank you for your payment."}
        </Text>
      </Page>
    </Document>
  );
};

const normalizePaymentSource = (src) => {
  const s = String(src || "").trim().toLowerCase();

  if (s === "medicalaid" || s === "medical aid") return "Medical Aid";
  if (s === "patient") return "Patient";

  // keep existing values (e.g. "Unallocated Funds", "EFT", etc.)
  return src || "";
};


export async function ExportPDFReceipt(receipt, { asBlob = false } = {}) {
  try {
    if (!receipt) throw new Error("Receipt data is required");
    const blob = await pdf(<ReceiptPDF receipt={receipt} />).toBlob();
    if (asBlob) return blob;
    const blobURL = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobURL;
    
    // Use appropriate filename based on document type
    const filename = receipt.receiptNumber.startsWith("CN-") 
      ? `Credit_Note_${receipt.receiptNumber}.pdf`
      : `Receipt_${receipt.receiptNumber}.pdf`;
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(blobURL);
    }, 100);
    return blob;
  } catch (error) {
    console.error("Error generating receipt PDF:", error);
    throw error;
  }
}